import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send } from "lucide-react";

export function MessageThread({ applicationId, title }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get(`/threads/${applicationId}/messages`).then(({ data }) => setMsgs(data)).catch(() => setMsgs([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [applicationId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(`/threads/${applicationId}/messages`, { text }); setText(""); await load(); }
    finally { setSending(false); }
  };

  return (
    <div className="flex h-[420px] flex-col" data-testid="message-thread">
      {title && <p className="mb-2 text-sm font-semibold text-[#003057]">{title}</p>}
      <div className="flex-1 space-y-2 overflow-y-auto rounded-xl bg-slate-50 p-3">
        {!msgs && <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#003057]" /></div>}
        {msgs && msgs.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No messages yet. Say hello!</p>}
        {msgs && msgs.map((m) => {
          const mine = m.sender_email === user?.email;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-[#003057] text-white" : "bg-white text-slate-700 border border-slate-200"}`}>
                {!mine && <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-[#5fa114]">{m.sender_name || m.sender_role}</p>}
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex gap-2">
        <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" />
        <Button data-testid="message-send" onClick={send} disabled={sending} className="rounded-full bg-[#78be21] font-bold text-[#00243f] hover:bg-[#6aac1c]">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
